// owner-map: OP-03b.spa
// Branch-creation command (EPIC #157, phase P5.2 — design §2.2).
//
// `createBranch` moved VERBATIM out of `App.jsx` — the unified branch exec path
// (placeholder mint → exec claim → streamBranch → terminal dual-write). The only
// behavioral rewrites are the migration-mandated ones:
//   • `runStateRef.current`    → `deps.store.getState()`   (retires §7 #1)
//   • `execByRunIdRef.current` → the `execTable` module singleton (§7 #3 claim/release)
//   • `__authEnabled`/`__tourFixture`/`__tourBranchIncrease` → threaded tour getters (§7 #8)
//   • I/O (`streamBranch`) through `deps.services.forecastClient` (design §2.2)
// The §7 #3 exec claim/release-in-each-exit is byte-preserved; abort is caller-owned
// (the `handle*` adapters mint the AbortController and pass `opts.signal`).
//
// The two UI adapters (`handleBranch`/`handleCustomEventBranch`) and their dispatch
// helpers stay in React — `hooks/useBranchForecast.js` — because they are pure
// clicked-row/DOM/render-state glue a React-free command can't hold.
import { progressPayload } from "../lib/forecast/progress.js";
import { makeRun, STATUS } from "../lib/forecast/model.js";
import {
  runCreated,
  contractKeySet,
  forkEventSet,
  traceAllocated,
  runProgress,
  runCompleted,
  runErrored,
  runRemoved,
} from "../lib/forecast/reducer.js";
import { pickedTier, rootEngine } from "../lib/forecast/engines.js";
import { selectRun } from "../lib/forecast/selectors.js";
import { buildBranchContext, triggerToAssumption } from "../lib/forecast/branchTrigger.js";
import { buildReforecastPreprocessed } from "../lib/forecast/reforecastRequest.js";
import { outcomeSpaceOfRun } from "../lib/forecast/outcomeSpace.js";
import { sourceEventIdentity } from "../lib/forecast/derivedBranch.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import { execTable } from "../store/execTable.js";
import { applyNarrationFrame } from "../lib/forecast/narrationRouter.js";
import { applyUpdateFrame } from "../lib/forecast/updateRouter.js";
import { liveEventsReconcileInPlace, projectLiveEvents } from "../lib/forecast/liveTriggers.js";
import { bindIntent } from "../lib/telemetry/intentDeps.js";
import {
  beginIntent,
  type BeginIntentOptions,
  type IntentHandle,
} from "../lib/telemetry/instrument.js";
import type { CommandDeps } from "./deps.js";

type AnyFn = (...args: any[]) => any;

/** A branch trigger descriptor consumed by `triggerToAssumption`. */
export interface BranchTrigger {
  kind: "event-mod" | "custom-event" | "reforecast";
  [k: string]: any;
}

/**
 * Per-site extras threaded through the shared exec path.
 *
 * `signal` and `abortController` MUST be the same controller: `signal` drives the branch
 * stream and every abort check; `abortController` is stored on the exec-table entry so a
 * registry-driven cancel (`execTable.abort(runId)`) fires that same controller. Both call
 * sites pass `{ signal: ac.signal, abortController: ac }`. (An `abortController`-only caller
 * is not a supported path — the exec handle isn't read for stream cancellation.)
 */
export interface BranchOpts {
  signal?: AbortSignal | null;
  abortController?: AbortController | null;
  onProgress?: AnyFn;
  onComplete?: AnyFn;
  /** Resolve extra facts about the trigger AFTER the placeholder is drawn and before the
   *  stream opens — e.g. preprocessing a typed trigger into a real contract. Receives the
   *  branch's run id so the caller can attach UI to the run it just created — the typed
   *  trigger's confirm panel gates on it. Returning null (or throwing) leaves the branch to
   *  stream with the un-enriched trigger, unless `enrichRequired` is set. */
  enrichTrigger?: (trigger: unknown, branchRunId: string) => Promise<unknown | null>;
  /** The enrichment is what makes the trigger a contract, and every trigger must be one:
   *  when it yields nothing, the placeholder is removed and no branch runs. The caller's
   *  `enrichTrigger` owns telling the user why. */
  enrichRequired?: boolean;
  /** Called with the branch's runId once `enrichTrigger` succeeded, before the stream opens. */
  onEnriched?: AnyFn;
  /** Called with the branch's runId as soon as it exists, BEFORE the stream opens.
   *  The seam the typed-trigger confirm panel attaches to: the panel is keyed by
   *  clientId (== runId) and must appear WHILE the branch runs, exactly as the
   *  submit path's panel does — so it cannot wait for onComplete.
   *
   *  NOT WIRED YET: no caller passes this, so a branch's preprocessed question is
   *  never offered for confirmation. The panel's own gate already admits branches
   *  (App.jsx), so wiring a `setPendingPreprocess` here is all that is missing. */
  onCreated?: AnyFn;
  /** The engine the caller has already resolved, shown and (for a deep inheritance)
   *  had confirmed — the #5636 guard in useBranchForecast. When set it is what runs and
   *  what is stamped on the run, so the value the user agreed to is the value sent; the
   *  live-parent resolution below is only the fallback for callers that pass none. */
  engine?: string | null;
  /** Stream from the forecast server even while the tour fixture is armed. The fixture's
   *  canned branch result suits the tour's own triggers; it says nothing about a trigger
   *  the user typed. Needs a parent with a server forecastId. */
  live?: boolean;
  /** The control that started it (telemetry `origin`); defaults to the trigger's Branch. */
  origin?: BeginIntentOptions["origin"];
  /** An intent already open for this branch (a derived branch's fallback, deriveBranch). */
  intent?: IntentHandle<"forecast.branch">;
  /** With `intent`: the branch id it named, reused for this run. */
  branchRunId?: string;
  [k: string]: any;
}

/** Deps for the branch command: the shared base + the React handles it needs. */
export interface BranchDeps extends CommandDeps {
  ui: { forecastEngine: string; forecastLiveProgressEnabled?: boolean };
  refs?: {
    forecastLiveProgressEnabledRef?: { current: boolean };
    slideCache?: { current: Record<string, unknown> };
  };
  callbacks: { startBranchAnim: AnyFn; setLastObs: AnyFn };
  tour: { getTourFixture: AnyFn; getAuthEnabled: AnyFn; getTourBranchIncrease: AnyFn };
}

export interface BranchArgs {
  parentRunId: string;
  trigger: BranchTrigger;
  opts?: BranchOpts;
}

// uid: random unique id with a graceful fallback (mirrors App.jsx's module-level helper).
const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

/**
 * Create a branch off `parentRunId` under `trigger`. Parent identity is resolved FROM
 * THE STORE (never a closure) — the headline fix baked into the original createBranch.
 */
export async function branchForecast(
  rawDeps: BranchDeps,
  { parentRunId, trigger, opts = {} }: BranchArgs,
): Promise<void> {
  if (trigger._live === true || trigger.event?._live === true) return;
  // The user's branch (P2 design §4.6): it follows the branch run, wherever the user is
  // meanwhile; the exits below that leave no run end it here.
  // A derived branch that fell back to this model run continues its intent (deriveBranch),
  // under the branch id that intent already named.
  const branchRunId = (opts.intent && opts.branchRunId) || "br-" + uid();
  // 1) Parent identity comes from THE STORE, never a closure, and from BOTH
  //    slices — a collaborator may branch a forecast shared with them. A miss
  //    here yields a null `parentForecastId`, which the proxy refuses as a
  //    branch with no parent.
  const parent = selectRun(rawDeps.store.getState(), parentRunId);
  // The branch inherits its PARENT's engine (prefer the store run's durable
  // backend; serverContext may be null for in-flight/resumed parents, so
  // rootEngine alone would fall back to the global engine). Resolved BEFORE the
  // run is created so it can be stamped onto the branch run below — otherwise the
  // branch's backend is left null and the write-once traceAllocated later stamps
  // it from the server's generic `x-forecast-backend` header ("sooth-forecast"),
  // so EVERY branch chat line mislabels as that instead of its root's engine.
  // `opts.engine` wins when the adapter resolved (and gated) the engine already — see
  // BranchOpts.engine — so a re-read of the live parent here cannot dispatch a different
  // engine from the one that was confirmed. Resolved before the intent opens, which names
  // its depth.
  const branchEngine =
    opts.engine || parent?.backend || rootEngine(parent) || rawDeps.ui.forecastEngine;
  const intent =
    opts.intent ??
    beginIntent(
      "forecast.branch",
      {
        forecast_id: branchRunId,
        parent_id: parentRunId,
        trigger_id: trigger.event?.id ?? trigger.eventId ?? null,
        direction: trigger.sense ?? trigger.direction ?? null,
        tier: pickedTier(branchEngine),
      },
      { origin: opts.origin ?? "trigger.branch" },
    );
  intent.attachRun(branchRunId);
  const deps = bindIntent(rawDeps, intent);
  const { store, services } = deps;
  const dispatchRun = store.dispatch;
  const { streamBranch } = services.forecastClient;
  const { forecastLiveProgressEnabled } = deps.ui;
  const { startBranchAnim, setLastObs } = deps.callbacks;
  const { getTourFixture, getAuthEnabled, getTourBranchIncrease } = deps.tour;
  const tourFixture = opts.live ? null : getTourFixture();
  const parentForecastId = parent?.forecastId ?? null;
  const conversationId = parent?.conversationId ?? parentRunId;
  // A click on the lane root (parentRunId === conversationId) makes a top-level
  // branch (parentBranchId null); a click on a branch row makes a sub-branch
  // off that branch.
  const parentBranchId = parentRunId === conversationId ? null : parentRunId;

  // 2) Assumption / display fields / fork event / fork date (shared with genBranch).
  //    forkDate is the branch's visual fork position: for event-mod it is the
  //    ORIGINAL event's visual midpoint (NOT the post-modification date, so a
  //    date-move still forks from where the event sat); custom-event → the new
  //    event's date; reforecast → the trigger's pass-through forkDate.
  // `triggerToAssumption` is a still-JS module; its inferred param/return shapes are
  // too narrow (no `sense`, a concrete trigger literal). Cast at the boundary (decision C).
  // `let`, not `const`: an `enrichTrigger` hook (below) may resolve extra facts about the
  // trigger AFTER the placeholder is on screen and re-derive these. See that block.
  let { assumption, modification, forkEvent, forkDate, sense } = triggerToAssumption(
    trigger as any,
    uid,
  ) as any;
  if (trigger.kind === "event-mod" && forkEvent) {
    const identity = sourceEventIdentity(trigger.event as any);
    if (identity) forkEvent = { ...forkEvent, _sourceEventIdentity: identity };
  }

  // The branch forecasts the root's contract (authored question, criteria, background, date)
  // with the assumption laid on top by the server — the same rebuild the reforecast path uses.
  // Sourced from the conversation ROOT so a sub-branch never nests conditional titles.
  const rootRun = store.getState().runsById[conversationId];
  // Multi-value ("multiway") parent: carry its outcome space so the branch reforecast is
  // ALSO a distribution (genForecastFromServer adds body.outcome_space when this is set).
  // Same lookup as the reforecast fan-out: the parent's live contract, then its history
  // vintages, then the root's — so a root whose live contract lost its space to an earlier
  // binary rerun still branches as a distribution instead of being re-authored binary.
  // `outcomeSpaceOfRun` rejects a BooleanSpace at every step — a binary parent's
  // `forecast_value` is its P(YES) as a PMF, not a question outcome space — so a binary
  // parent yields null and an ordinary binary branch.
  // NOTE: this must NOT change `branchEngine` — the run's `backend` stays the real engine,
  // or the chat line / resume mislabels.
  const outcomeSpace = outcomeSpaceOfRun(parent) || outcomeSpaceOfRun(rootRun);
  const parentQuestionText = rootRun?.question ?? parent?.question ?? "";
  const contractSource = (rootRun as any)?.contract ?? (parent as any)?.contract ?? null;
  const preprocessedQuestion =
    contractSource && (contractSource.question || contractSource.resolution_criteria)
      ? buildReforecastPreprocessed(
          contractSource,
          contractSource.question || parentQuestionText,
          outcomeSpace,
        )
      : null;

  // Branch inherits the trigger-LLM of the EXACT lane it forks from, read from
  // the parent run's stamped `triggerModel` (the effective wire value recorded
  // when that run was created; see useForecastSubmit). This is immune to
  // same-engine compares and to live Engine-A/B dropdown swaps between submit
  // and branch — the old heuristic compared the live compareEngine against the
  // frozen branchEngine, which mis-attributed when both legs shared an engine.
  // null → server default: single-forecast roots stamp triggerModel:null, so
  // their branches never inherit a debug trigger LLM.
  const branchTriggerModel = parent?.triggerModel ?? null;

  // 3) Mint + dispatch the placeholder branch run, then claim the exec slot.
  //    backend:branchEngine is stamped at creation (write-once in the reducer) so
  //    the run-projected chat line is attributed to the parent's engine, not the
  //    header value a later traceAllocated would otherwise win with.
  dispatchRun(
    runCreated(
      makeRun({
        runId: branchRunId,
        conversationId,
        parentRunId,
        status: STATUS.RUNNING,
        forkDate,
        modification,
        forkEvent,
        backend: branchEngine,
        triggerModel: branchTriggerModel,
      }),
    ),
  );
  if (
    !execTable.claim(branchRunId, {
      kind: "branch",
      clientId: branchRunId,
      abortController: opts.abortController || null,
    })
  ) {
    // id collision (two branches within the same ms) — undo the run and bail
    // rather than proceed without a lock (mirrors the per-site guards).
    dispatchRun(runRemoved(branchRunId));
    intent.end("error");
    return;
  }
  // Branch reveal animation — placeholder pass (verbatim parity with the old
  // handleBranch: startBranchAnim(placeholderId,1) right after the exec claim).
  // The vertical-connector grow runs against the single placeholder event; the
  // event-reveal sweep below replays once the real branch events land.
  startBranchAnim(branchRunId, 1);
  // Fire before the stream opens. Wrapped: a caller's panel bookkeeping must never
  // abort a branch that is otherwise fine.
  try {
    opts.onCreated?.(branchRunId);
  } catch (_e) {
    /* caller-side only — never fail the branch for it */
  }

  // ── Late trigger enrichment ────────────────────────────────────────────────
  // Runs HERE, deliberately: after `runCreated` + `startBranchAnim` have already put the
  // node and its dotted branch lines on screen, and before the stream opens.
  //
  // A typed trigger has to go through the preprocessor to become a real contract, which is
  // an LLM call. Doing that BEFORE the branch existed meant the user clicked the timeline
  // and nothing appeared for seconds — the branch looked like it had not registered. Drawing
  // the placeholder first and enriching after costs nothing and makes the click feel
  // instant, which is the whole point.
  //
  // The re-derived fork event KEEPS THE PLACEHOLDER'S ID so React's key is stable: a new id
  // would remount the node and restart its reveal animation mid-flight, which is exactly the
  // stutter this ordering exists to avoid.
  let enrichedOk = false;
  if (opts.enrichTrigger) {
    try {
      const enriched = await opts.enrichTrigger(trigger, branchRunId);
      if (enriched) {
        const next = triggerToAssumption(enriched as any, uid) as any;
        assumption = next.assumption;
        modification = next.modification;
        sense = next.sense;
        // forkDate is NOT taken from the enrichment: the node is already drawn where the
        // user clicked, and moving it now would make it jump.
        forkEvent = { ...next.forkEvent, id: forkEvent?.id ?? next.forkEvent?.id };
        dispatchRun(forkEventSet(branchRunId, forkEvent));
        enrichedOk = true;
      }
    } catch (_e) {
      // Without `enrichRequired` the branch streams with the un-enriched trigger.
    }
    if (!enrichedOk && opts.enrichRequired) {
      // No contract, no branch: undo the placeholder exactly as the claim-collision path does.
      dispatchRun(runRemoved(branchRunId));
      execTable.release(branchRunId);
      intent.end("cancelled");
      return;
    }
  }

  // An edit can remove this placeholder while enrichment is pending.
  if (opts.signal?.aborted || !selectRun(store.getState(), branchRunId)) {
    execTable.release(branchRunId);
    intent.end("cancelled");
    return;
  }

  // AFTER the cancellation check, deliberately: an enrichment can resolve successfully
  // after its abort fired (a scoring call turns an abort into a failed score, not a throw),
  // and `onEnriched` is where an edit-resubmit removes the branch being replaced — so
  // running it first would destroy the original for a replacement that is about to be
  // cancelled.
  if (enrichedOk) {
    try {
      opts.onEnriched?.(branchRunId);
    } catch (_e) {
      /* caller-side only — never fail the branch for it */
    }
  }

  // The branch's running + terminal chat lines are RUN-PROJECTED —
  // selectChatProjection emits the "Generating Branch…" spinner (status:running) and
  // the "Branch: <mod> — Prediction: N%" / error line (terminal) from the branch run's
  // store state. No setMsgs placeholder/result write here; the store IS the source.

  try {
    // 4) Stream via the single branch SSE path (streamBranch). branchEngine was
    //    resolved above (and stamped onto the branch run) so attribution is durable.
    // Branch soft-context: hand the branch re-forecast the parent's PRIOR to revise
    // (headline p0 + report reasons + the clicked trigger's a/b) instead of running
    // cold. Assembled by the shared model-layer builder.
    const branchContext = buildBranchContext(parent, trigger);
    const {
      contract,
      serverPrediction,
      events,
      serverContext,
      serverExplanation,
      report,
      stampedForecastId,
      _obs: branchObs,
      premiseKey,
      premiseContract,
    } = await streamBranch(
      // F1: a BRANCH run's own `question` is "" (makeRun default), so a sub-branch
      // forked off a branch would stream an empty base question. The conversation
      // ROOT carries the real question — parentQuestionText sources it from there,
      // falling back to the immediate parent then "".
      parentQuestionText,
      branchEngine,
      assumption,
      opts.signal || null,
      {
        parentForecastId,
        clientId: branchRunId,
        // Fork metadata — createBranch has forkDate/modification/forkEvent in
        // scope (from triggerToAssumption). Thread them so streamBranch can
        // forward them into the request body via genForecastFromServer, ensuring
        // the server persists a non-null fork column on the INSERT (C1 fix).
        forkDate,
        modification,
        forkEvent,
        branchContext,
        branchSense: sense,
        // Multi-value parent → the branch reforecast runs on the distribution lane too.
        outcomeSpace,
        // The root's contract as a preprocessed question (null when the parent has none).
        preprocessedQuestion,
        postprocessModel: branchTriggerModel || null,
        onProgress: (p: any) => {
          // Dispatch live SSE progress to the store so the
          // run-projected branch line animates IN-SESSION (mirrors
          // reattachBranch:3732). Without this, run.progress stays null and the
          // line shows the static "Generating forecast…" placeholder until a
          // reload — only the reattach path used to dispatch progress, which is
          // why progress appeared only after the first reload. opts.onProgress
          // (per-site sinks, e.g. reforecast) still fires after.
          try {
            const stage = p?.stage ?? null;
            const step = p?.step ?? null;
            // Dispatch on EITHER stage or step — progressPayload resolves a step index
            // from `step` alone (e.g. a stage-less `consolidating` frame). Mirrors resume.
            if (stage != null || step != null) {
              dispatchRun(
                runProgress(
                  branchRunId,
                  progressPayload(stage, p?.progress, step, p?.data, branchEngine),
                ),
              );
            }
          } catch (_e: any) {
            /* progress is best-effort */
          }
          opts.onProgress?.(p);
        },
        onUpdate: forecastLiveProgressEnabled
          ? (frame: unknown) => applyUpdateFrame(dispatchRun, store.getState, branchRunId, frame)
          : undefined,
        onNarration: forecastLiveProgressEnabled
          ? (frame: unknown) => applyNarrationFrame(dispatchRun, store.getState, branchRunId, frame)
          : undefined,
        // The branch's own contract key, when the stream names one, so the live run is read on
        // it after a reconnect like a loaded one.
        onContractKey: (p: any) => {
          if (typeof p?.key === "string" && p.key)
            dispatchRun(contractKeySet(branchRunId, p.key, p?.key_status ?? null));
        },
        onTraceAllocated: ({ traceId, forecastId, backend }: any) => {
          // Stamp traceId/forecastId/backend on the branch run. The store NEEDS the
          // traceId so the unified resume sweep's kind:'branch' path can recover this
          // branch from its persisted server row after a reload (the sole recovery
          // source — there is no sessionStorage branch handle).
          dispatchRun(traceAllocated(branchRunId, { traceId, forecastId, backend }));
          // engine:branchEngine — the branch's inherited orchestrator (== run.backend,
          // stamped at creation). Lets the bug-triage Traces widget label branch rows
          // with their engine instead of "?" (branch.trace_allocated otherwise carries none).
          recordUiEvent({
            event: "branch.trace_allocated",
            branchRunId,
            traceId: traceId || null,
            forecastId: forecastId || null,
            engine: branchEngine,
          });
        },
        tourFixture,
        tourBranchIncrease: getTourBranchIncrease(),
      },
    );
    // Aborted: release the exec claim before bailing. Previously this returned
    // WITHOUT releasing, leaking the branch's slot — which both blocked a later
    // re-claim of that branch and left the exec table non-empty, so the
    // loading-reconcile below could never see "nothing in flight".
    if (opts.signal?.aborted) {
      execTable.release(branchRunId);
      intent.end("cancelled");
      return;
    }
    // 5a) Terminal success — dual-write completion to the store, release the lock.
    execTable.release(branchRunId);
    // Save-time filter: keep only events whose VISIBLE position (mean_date when
    // present, else date) is AFTER the fork. Matches the renderer filter at ~:8239
    // and OLD handleBranch's pre-runCompleted filter (c671a841f:4537-4544). Prevents
    // pre-fork events from leaking into the stored run and keeps the reveal-animation
    // count in sync with what the renderer actually draws.
    const finalForkMs = new Date(forkDate).getTime();
    const afterFork = (candidateEvents: any[]) =>
      candidateEvents.filter((e: any) => {
        if (!e.date) return false;
        const ed = new Date(e.date).getTime();
        if (isNaN(ed)) return false;
        const md = e.mean_date ? new Date(e.mean_date).getTime() : NaN;
        const visMs = isNaN(md) ? ed : md;
        return isNaN(finalForkMs) || visMs > finalForkMs;
      });
    const filteredEvents = afterFork(events || []);
    dispatchRun(
      runCompleted(branchRunId, {
        contract,
        serverPrediction,
        events: filteredEvents,
        serverContext,
        serverExplanation,
        report,
        obs: branchObs,
      }),
    );
    // A typed premise's key and wording arrive with the run's result (the server stamps the
    // stored fork the same way), so the live run carries them: a reconnect then reads this
    // branch on its premise's key, and a sub-branch can name the premise.
    if (premiseKey && !(forkEvent as any)?.contract_key) {
      forkEvent = {
        ...(forkEvent as any),
        contract_key: premiseKey,
        ...(premiseContract ? { contract: premiseContract } : {}),
      };
      dispatchRun(forkEventSet(branchRunId, forkEvent));
    }
    // A branch is user-initiated forecast activity, so the observability panel's
    // "last …" should track it (submit/resume already call setLastObs; branches
    // previously left the panel showing the spine's obs). Mirrors run.obs.
    if (branchObs) setLastObs({ ...branchObs, ts: new Date().toISOString() });
    // Tour fixture branch: it never streamed through the proxy (genForecastFromServer
    // is short-circuited), so it has NO server row (stampedForecastId is null) and
    // would vanish on reload. Eager-create its branch row — parent_forecast_id + fork —
    // the same way startTour eager-creates the example forecast root, then stamp the
    // returned UUID so the branch joins the normal persistence/hydration flow.
    if (tourFixture && parentForecastId && getAuthEnabled() && !stampedForecastId) {
      try {
        const branchWire = {
          id: branchRunId,
          question: "",
          contract,
          events: filteredEvents,
          serverPrediction: typeof contract?.probability === "number" ? contract.probability : 0,
          serverExplanation: serverExplanation || "",
          serverContext: serverContext || {},
          report: report || null,
          _series: {},
          parentId: parentForecastId,
          fork: {
            forkDate: forkDate ?? null,
            modification: modification ?? null,
            forkEvent: forkEvent ?? null,
          },
        };
        // Tour-only eager-create, persisted through the typed api seam (P5.11). saveForecast
        // never throws and returns the server-allocated forecastId (or ok:false on failure),
        // so the branch just won't persist on a non-ok/error.
        const br = await deps.services.api.saveForecast(branchWire);
        if (br.ok && br.forecastId) {
          dispatchRun(traceAllocated(branchRunId, { forecastId: br.forecastId }));
        }
      } catch (_e: any) {
        /* graceful: branch just won't persist this session */
      }
    }
    // Branch reveal animation — completion pass for legacy/non-reconciled streams.
    // A typed stream has already placed the same trigger IDs on the lane, so replaying
    // the sweep at done would remove and recreate live nodes that should reconcile.
    const futureEvts = (filteredEvents || []).filter((e: any) => !e.isPast);
    const progressDoc = store.getState().runsById[branchRunId]?.progressDoc;
    const liveEvents = progressDoc ? afterFork(projectLiveEvents(progressDoc)) : [];
    const reconciledLiveEvents =
      (deps.refs?.forecastLiveProgressEnabledRef?.current ??
        forecastLiveProgressEnabled === true) &&
      liveEventsReconcileInPlace(liveEvents, filteredEvents);
    if (!reconciledLiveEvents) {
      // Branch anchors are owner-scoped as `branch-<runId>::<eventId>`. A revisable
      // trigger can retain its ID while final timing moves, so clear this branch's
      // cached geometry before replaying its authoritative reveal.
      const cache = deps.refs?.slideCache?.current;
      if (cache) {
        const prefix = `branch-${branchRunId}::`;
        for (const key of Object.keys(cache)) if (key.startsWith(prefix)) delete cache[key];
      }
      startBranchAnim(branchRunId, futureEvts.length);
    }
    // The branch result line ("Branch: <mod> — Prediction: N%") is
    // run-projected from the COMPLETED branch run via selectChatProjection — no
    // setMsgs/persistMessage here. The branch run's terminal state IS the record,
    // so a mid-branch reload reconstructs the same line from the hydrated run.
    // Per-site post-completion hook (e.g. the custom-event direction flip,
    // series-impact estimation). The branch run is already COMPLETED in the
    // store; the hook layers per-site behavior on top.
    const branchResult = {
      runId: branchRunId,
      forecastId: stampedForecastId,
      parentForecastId,
      parentBranchId,
      forkDate,
      modification,
      forkEvent,
      events: filteredEvents,
      probability: contract?.probability ?? 0.5,
      contract,
      serverContext,
      serverExplanation,
      report,
    };
    if (typeof opts.onComplete === "function") {
      try {
        await opts.onComplete(branchResult);
      } catch (_e: any) {
        /* per-site hook failure must not strand the branch */
      }
    }
  } catch (e: any) {
    // Aborted: release the exec claim before bailing (see the success-path note).
    if (e?.name === "AbortError" || opts.signal?.aborted) {
      execTable.release(branchRunId);
      intent.end("cancelled");
      return;
    }
    // 5b) Terminal error — dual-write to the store, release the lock. Run RETAINED.
    execTable.release(branchRunId);
    dispatchRun(runErrored(branchRunId, e));
    // The branch error line is run-projected from the ERRORED branch
    // run via selectChatProjection (it renders run.error as the terminal content).
    // No setMsgs/persistMessage — the run's terminal error state is the record.
  }
}
